import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.tsx';
import { ReactNode } from 'react';

interface ProtectedRouteProps {
    children: ReactNode;
    requiredRole?: 'ADMIN' | 'MANAGER' | 'USER';
}

export default function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-secondary-50">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    <p className="mt-4 text-secondary-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Check role-based access
    if (requiredRole) {
        const roleHierarchy = { ADMIN: 3, MANAGER: 2, USER: 1 };
        const userRoleLevel = roleHierarchy[user.role];
        const requiredRoleLevel = roleHierarchy[requiredRole];

        if (userRoleLevel < requiredRoleLevel) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-secondary-50">
                    <div className="card max-w-md">
                        <h2 className="text-danger-600 mb-4">Access Denied</h2>
                        <p className="text-secondary-700">
                            You do not have permission to access this page.
                        </p>
                        <p className="text-sm text-secondary-500 mt-2">
                            Required role: {requiredRole}
                        </p>
                    </div>
                </div>
            );
        }
    }

    return <>{children}</>;
}
